/**
 * The diagnosis picker offers a fixed list of symptoms plus an "Other" chip that
 * unlocks a free-text box. "Other" is a UI affordance, not a symptom: it must
 * never reach the appointment on its own, and it shouldn't be appended to the
 * customer's own words either. Both funnel entry points (the new-quote flow and
 * the dashboard's book-again modal) build their concern through here.
 */

export const OTHER_DIAGNOSIS_OPTION = 'Other';

const isOther = (option: string): boolean =>
  option.trim().toLowerCase() === OTHER_DIAGNOSIS_OPTION.toLowerCase();

/** Whether the customer can continue: at least one symptom, and if "Other" is
 *  among them, a real description of the problem. */
export const isDiagnosisSelectionComplete = (
  problems: string[] | undefined,
  description: string | undefined
): boolean => {
  const picked = problems ?? [];
  if (picked.length === 0) return false;
  if (picked.some(isOther)) return (description ?? '').trim() !== '';
  return true;
};

/** The `diagnosis_input` sent to the API: the customer's description first,
 *  then the named symptoms, with the "Other" placeholder dropped. */
export const buildDiagnosisInput = (
  problems: string[] | undefined,
  description: string | undefined
): string => {
  const symptoms = (problems ?? []).filter((option) => !isOther(option));
  const text = (description ?? '').trim();
  if (text && symptoms.length) return `${text}. ${symptoms.join(', ')}`;
  if (text) return text;
  return symptoms.join(', ');
};
